import React from "react";
import { useEditOrderIndState } from "../../../data/trading/orderIndicative/hooks/useEditOrderIndState";
import { useOrderIndValidator } from "../../../data/trading/orderIndicative/hooks/useOrderIndValidator";
import { useOrderIndUpsert } from "../../../data/trading/orderIndicative/hooks/useOrderIndUpsert";
import { RouteComponentProps, useHistory } from "react-router-dom";
import { usePageStyles } from "../../pageStyles";
import { LocalizationContext } from "../../../core/localization/Localization";
import { Card, Grid, CardHeader, Divider, CardContent, InputAdornment } from "@material-ui/core";
import { FabSave } from "../../components/common/FabSave";
import { OrderIndData } from "../../../data/trading/orderIndicative/types";
import { StringInput } from "../../components/common/StringInput";
import { LeavePrompt } from "../../components/common/LeavePrompt";
import { orderIndLocation } from "./Locations";
import { PageTitle } from "../../components/common/PageTitle";
import { OrderSideSelect } from "../orders/OrderSideSelect";
import { ProductSelect } from "../../components/listing/ProductSelect";
import { ProductSelectData } from "../../../data/listing/products/types";
import { NumberInput } from "../../components/common/NumberInput";
import { OrderSide, Status } from "../../../data/models";
import { BoolInput } from "../../components/common/BoolInput";


export interface EditOrderIndInfo {
	orderIndId?: string;
}

export interface EditOrderIndProps extends RouteComponentProps<EditOrderIndInfo> { }

export const EditOrderIndPage: React.FunctionComponent<EditOrderIndProps> = props => {
	const { getString } = React.useContext(LocalizationContext);
	const pageStyles = usePageStyles();

	const [orderInd, editOrderInd, changed] =
		useEditOrderIndState(
			props.match.params.orderIndId
				? Number(props.match.params.orderIndId)
				: undefined
		);
	const [validationState, validate] = useOrderIndValidator();
	const [fetchState, upsert] = useOrderIndUpsert();
	const history = useHistory();
	const [selectedProduct, setSelectedProduct] = React.useState<ProductSelectData | null>(null);
	const statusProducts = Status.Active

	const unsavedChanges = fetchState.type === "not-started" && changed;

	const handleChange = React.useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			editOrderInd(event.target.name as any, event.target.value);
		},
		[editOrderInd]
	);

	const save = React.useCallback(() => {
		if (fetchState.type === "successful") {
			history.push(orderIndLocation);
		} else if (fetchState.type !== "started") {
			const normalized = validate(orderInd);
			if (normalized) {
				upsert(normalized);
			} else {
			}
		}
	}, [orderInd, validate, fetchState, history, upsert]);

	const pageTitle = orderInd && orderInd.orderIndId ? "orderEditTitle" : "orderCreateTitle";

	if (!orderInd) {
		return null;
	}

	return (
		<Grid container className={pageStyles.root}>

			<Grid item xs={12} className={pageStyles.gridItem}>
				<Card>
					<CardHeader title={<PageTitle title={pageTitle} backLocation={orderIndLocation} />} />
					<Divider />
					<CardContent>
						<Grid container direction="row" justify="flex-start" alignItems="center" spacing={3}>

							<Grid item xs={12} sm={2}>
								<OrderSideSelect
									handleChange={(event: React.ChangeEvent<HTMLInputElement>) =>
										editOrderInd("side", event.target.value as OrderSide)
									}
									side={orderInd.side}
								/>
							</Grid>
						</Grid>
						<Grid container direction="row" justify="flex-start" alignItems="center" spacing={3}>
							<Grid item xs={12} sm={3}>
								<ProductSelect
									label={getString("orderProduct")}
									status={statusProducts}
									predicate={prod => prod.prodId === orderInd.productId
									} onChangeItem={prod => {
										editOrderInd("productId", prod ? prod.prodId : undefined);
										setSelectedProduct(prod);
									}}
									required={validationState.productId !== undefined}
									error={validationState.productId !== undefined && !validationState.productId}
								/>
							</Grid>
						</Grid>
						<Grid container direction="row" justify="flex-start" alignItems="center" spacing={3}>
							<Grid item xs={12} sm={3}>
								<StringInput<OrderIndData>
									property="price"
									item={orderInd}
									validationState={validationState}
									onChange={handleChange}
									label={getString("orderPriceWithoutVat")}
								/>
							</Grid>
						</Grid>
						<Grid container direction="row" justify="flex-start" alignItems="center" spacing={3}>
							<Grid item xs={12} sm={3}>
								<NumberInput<OrderIndData>
									property="qty"
									item={orderInd}
									validationState={validationState}
									onChange={handleChange}
									label={getString("orderQty")}
									InputProps={{
										endAdornment: selectedProduct ? (
											<InputAdornment position="end">{selectedProduct.unit}</InputAdornment>
										) : undefined,
									}}
								/>
							</Grid>
						</Grid>
						<Grid container direction="row" justify="flex-start" alignItems="center" spacing={3}>
							<Grid item xs={12} sm={6}>
								<StringInput<OrderIndData>
									property="description"
									item={orderInd}
									validationState={validationState}
									onChange={handleChange}
									label={getString("orderIndDescr")}
								/>
							</Grid>
						</Grid>
						<Grid container direction="row" justify="flex-start" alignItems="center" spacing={3}>
							<Grid item xs={12} sm={6}>
								<StringInput<OrderIndData>
									property="parity"
									item={orderInd}
									validationState={validationState}
									onChange={handleChange}
									label={getString("orderIndParity")}
								/>
							</Grid>
						</Grid>
						<Grid container direction="row" justify="flex-start" alignItems="center" spacing={3}>
							<Grid item xs={12} sm={6}>
								<StringInput<OrderIndData>
									property="comment"
									item={orderInd}
									validationState={validationState}
									onChange={handleChange}
									label={getString("orderIndComment")}
								/>
							</Grid>
						</Grid>
						<Grid container item direction="row" justify="flex-start" alignItems="center" spacing={3}>
							<Grid item xs={1}>
								<BoolInput<OrderIndData>
									property="status"
									item={orderInd}
									onChange={handleChange}
									falsyValue={Status.Inactive}
									thrutyValue={Status.Active}
									label={getString("empStatus")}
								/>
							</Grid>
						</Grid>
						<FabSave fetchState={fetchState} onClick={save} />
						<LeavePrompt shouldShow={unsavedChanges} />
					</CardContent>
				</Card>
			</Grid>
		</Grid>
	);
};
